import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Inbox, Clapperboard, Wallet, Users, PauseCircle, PlayCircle, CircleDollarSign, Hourglass, RotateCcw, Briefcase, CheckCircle2, Activity, UploadCloud, PiggyBank } from "lucide-react";
import { API, authHeaders, JOB_STATUS_STYLES, JOB_STATUS_LABELS } from "@/constants/roles";
import { TasksPanel } from "@/components/admin/TasksPanel";
import { fmtMoney, fmtNum } from "@/utils/currency";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const AdminOverview = ({ onNavigate }) => {
  const [analytics, setAnalytics] = useState(null);
  const [budget, setBudget] = useState(null);
  const [subs, setSubs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [flags, setFlags] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/admin/analytics`, { headers: authHeaders() }),
      axios.get(`${API}/admin/budget`, { headers: authHeaders() }),
      axios.get(`${API}/admin/submissions`, { headers: authHeaders() }),
      axios.get(`${API}/admin/jobs`, { headers: authHeaders() }),
      axios.get(`${API}/admin/team`, { headers: authHeaders() }),
      axios.get(`${API}/admin/flags?status=open`, { headers: authHeaders() }),
    ])
      .then(([a, b, s, j, t, f]) => {
        setAnalytics(a.data);
        setBudget(b.data);
        setSubs(s.data);
        setJobs(j.data);
        setTeam(t.data);
        setFlags(f.data);
      })
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
      });
  }, [navigate]);

  const resolveFlag = async (id) => {
    try {
      await axios.patch(`${API}/admin/flags/${id}`, {}, { headers: authHeaders() });
      setFlags((fs) => fs.filter((f) => f.id !== id));
    } catch { /* stay */ }
  };

  useEffect(load, [load]);

  const resume = async () => {
    await axios.post(`${API}/admin/production/toggle`, { paused: false }, { headers: authHeaders() });
    load();
  };

  const activity = useMemo(() => {
    const events = [];
    subs.forEach((s) =>
      events.push({
        at: s.created_at,
        text: `${s.worker_name || "A worker"} submitted v${s.version} for "${s.job_title || "a job"}"`,
        go: "submissions",
      })
    );
    jobs.forEach((j) => {
      events.push({ at: j.created_at, text: `Job "${j.title}" was posted (${fmtMoney(j.estimated_pay)})`, go: "production" });
      if (j.claimed_at) events.push({ at: j.claimed_at, text: `${j.claimed_by_name || "A worker"} claimed "${j.title}"`, go: "production" });
    });
    return events.sort((a, b) => (b.at || "").localeCompare(a.at || "")).slice(0, 8);
  }, [subs, jobs]);

  if (!analytics || !budget) {
    return <p data-testid="admin-overview-loading" className="py-20 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading overview...</p>;
  }

  const js = analytics.jobs_by_status;
  const cards = [
    { icon: Clapperboard, label: "Active production jobs", value: js.active || 0, testid: "ov-active-jobs", go: "production" },
    { icon: Hourglass, label: "Awaiting review", value: js.awaiting_review || 0, testid: "ov-awaiting-review", go: "submissions" },
    { icon: RotateCcw, label: "In revision", value: js.in_revision || 0, testid: "ov-in-revision", go: "production" },
    { icon: Briefcase, label: "Available jobs", value: js.available || 0, testid: "ov-available-jobs", go: "production" },
    { icon: CheckCircle2, label: "Completed jobs", value: js.completed || 0, testid: "ov-completed-jobs", go: "production" },
    { icon: Inbox, label: "Pending applications", value: analytics.applications_by_status.pending || 0, testid: "ov-pending-apps", go: "applications" },
    { icon: Wallet, label: "Amount owed", value: fmtMoney(budget.owed), testid: "ov-owed", go: "payments" },
    { icon: PiggyBank, label: "Budget remaining", value: fmtMoney(budget.remaining), testid: "ov-budget-remaining", go: "budget" },
    { icon: CircleDollarSign, label: "Total paid", value: fmtMoney(budget.paid), testid: "ov-paid", go: "payments" },
  ];

  const recentSubs = subs.slice(0, 5);
  const topTeam = [...team].sort((a, b) => b.completed_jobs - a.completed_jobs).slice(0, 5);

  return (
    <div data-testid="admin-overview">
      <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Overview</h1>
      <p className="mt-1 text-sm text-[var(--pv-text-4)]">The whole Melto operation at a glance.</p>

      <div className="mt-6">
        <TasksPanel onNavigate={onNavigate} testid="owner-tasks-panel" title="Today's command list" />
      </div>

      {flags.length > 0 && (
        <div data-testid="owner-flags-card" className="mt-6 rounded-3xl border border-amber-500/30 bg-amber-500/10 p-6">
          <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-amber-300">
            <Activity className="h-4 w-4" /> Needs your attention ({flags.length})
          </p>
          <div className="mt-3 space-y-2">
            {flags.map((f) => (
              <div key={f.id} data-testid={`flag-row-${f.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/20 bg-[var(--pv-card)] px-4 py-3">
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${f.priority === "urgent" ? "border-rose-500/30 bg-rose-500/15 text-rose-300" : f.priority === "high" ? "border-amber-500/30 bg-amber-500/15 text-amber-300" : "border-[var(--pv-border)] text-[var(--pv-text-3)]"}`}>{f.priority}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-[var(--pv-text)]">{f.reason}</p>
                  <p className="text-[11px] text-[var(--pv-text-4)]">
                    {f.target_type} · flagged by {f.created_by} · {timeAgo(f.created_at)}
                    {f.note ? ` — ${f.note}` : ""}
                  </p>
                </div>
                <button data-testid={`flag-resolve-${f.id}`} onClick={() => resolveFlag(f.id)} className="rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1.5 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/25">
                  Resolved
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        data-testid="production-status-banner"
        className={`mt-6 flex flex-wrap items-center justify-between gap-3 rounded-3xl border px-6 py-5 ${
          budget.production_paused ? "border-rose-500/20 bg-rose-500/10" : "border-emerald-500/20 bg-emerald-500/10"
        }`}
      >
        <div className={`flex items-center gap-2.5 text-sm font-semibold ${budget.production_paused ? "text-rose-300" : "text-emerald-300"}`}>
          {budget.production_paused ? <PauseCircle className="h-5 w-5" /> : <PlayCircle className="h-5 w-5" />}
          {budget.production_paused
            ? `Production is paused${budget.pause_reason === "budget_reserve" ? " — budget reserve reached" : ""}. Workers can't claim new jobs.`
            : "Production is active — workers can claim jobs."}
        </div>
        {budget.production_paused && (
          <button
            data-testid="overview-resume-production-btn"
            onClick={resume}
            className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-600"
          >
            <PlayCircle className="h-4 w-4" /> Resume production
          </button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        {cards.map(({ icon: Icon, label, value, testid, go }) => (
          <button
            key={testid}
            data-testid={testid}
            onClick={() => onNavigate(go)}
            className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 text-left  transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <Icon className="h-5 w-5 text-cyan-400" />
            <p className="mt-3 font-display text-3xl font-bold tracking-tight text-[var(--pv-text)]">{value}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{label}</p>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">
              <UploadCloud className="h-4 w-4 text-cyan-400" /> Recent submissions
            </p>
            <button data-testid="ov-goto-submissions" onClick={() => onNavigate("submissions")} className="text-xs font-bold text-cyan-400 hover:text-cyan-300">
              View all
            </button>
          </div>
          {recentSubs.length === 0 ? (
            <p data-testid="ov-subs-empty" className="mt-4 text-sm text-[var(--pv-text-4)]">No submissions yet.</p>
          ) : (
            <ul data-testid="ov-recent-subs" className="mt-4 space-y-2.5">
              {recentSubs.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 rounded-2xl bg-[var(--pv-subtle)] px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[var(--pv-text-2)]">{s.job_title || "Job"} <span className="text-xs text-[var(--pv-text-4)]">v{s.version}</span></p>
                    <p className="text-xs text-[var(--pv-text-4)]">{s.worker_name || "Unknown"} · {timeAgo(s.created_at)}</p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${
                    s.status === "approved" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                    : s.status === "revision_requested" ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
                    : "bg-amber-500/15 text-amber-300 border-amber-500/30"
                  }`}>{s.status.replace(/_/g, " ")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">
            <Activity className="h-4 w-4 text-cyan-400" /> Recent activity
          </p>
          {activity.length === 0 ? (
            <p data-testid="ov-activity-empty" className="mt-4 text-sm text-[var(--pv-text-4)]">Nothing yet — post a job to get things moving.</p>
          ) : (
            <ul data-testid="ov-activity" className="mt-4 space-y-2.5">
              {activity.map((e, i) => (
                <li key={i}>
                  <button onClick={() => onNavigate(e.go)} className="flex w-full items-center justify-between gap-3 rounded-2xl bg-[var(--pv-subtle)] px-4 py-3 text-left transition-colors hover:bg-[var(--pv-raised)]">
                    <p className="truncate text-sm text-[var(--pv-text-2)]">{e.text}</p>
                    <span className="shrink-0 text-xs text-[var(--pv-text-4)]">{timeAgo(e.at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 ">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">
            <Users className="h-4 w-4 text-cyan-400" /> Team overview ({team.length})
          </p>
          <button data-testid="ov-goto-team" onClick={() => onNavigate("team")} className="text-xs font-bold text-cyan-400 hover:text-cyan-300">
            Manage team
          </button>
        </div>
        {team.length === 0 ? (
          <p data-testid="ov-team-empty" className="mt-4 text-sm text-[var(--pv-text-4)]">No workers yet — accept an application to build your crew.</p>
        ) : (
          <div data-testid="ov-team" className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {topTeam.map((w) => (
              <div key={w.id} className="rounded-2xl bg-[var(--pv-subtle)] px-4 py-3">
                <p className="truncate text-sm font-bold text-[var(--pv-text)]">{w.name || w.email}</p>
                <p className="mt-0.5 text-xs text-[var(--pv-text-4)]">
                  {w.worker_role || "Worker"} · {w.active_jobs} active · {w.completed_jobs} done
                  {w.amount_owed > 0 && <span className="text-amber-400 font-semibold"> · owed {fmtMoney(w.amount_owed)}</span>}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
